import { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Box, Button } from '@mui/material';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { type Editor } from '@ckeditor/ckeditor5-core';

import { theme } from '@frontend/theme';

import { useWorkflow } from '../WorkflowContext/hook';

enum TABS {
  EDITOR,
  PREVIEW,
}

interface CommentEditorModalProps {
  open: boolean;
  onClose: () => void;
  initialContent?: string;
  stepName: string;
  stepId?: string;
  readMode?: boolean;
}

const CommentEditorModal = ({
  open,
  onClose,
  initialContent = '',
  stepName,
  stepId,
  readMode = false,
}: CommentEditorModalProps) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<TABS>(readMode ? TABS.PREVIEW : TABS.EDITOR);
  const [content, setContent] = useState(initialContent);

  const { onEditStepField } = useWorkflow();

  const handleTabChange = (_e: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('workflows.instructions.title', { step: stepName })}</DialogTitle>
      <DialogContent>
        {!readMode && (
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t('workflows.instructions.tabs.edit')} />
            <Tab label={t('workflows.instructions.tabs.preview')} />
          </Tabs>
        )}
        <Box mt={2}>
          {tabValue === TABS.EDITOR && (
            <CKEditor
              editor={ClassicEditor}
              onChange={(_: any, editor: Editor) => {
                const data = editor.getData();
                setContent(data);
              }}
              data={content}
            />
          )}
          {tabValue === TABS.PREVIEW && (
            <Box
              sx={{
                '.ck-content': {
                  ...theme.typography.body2,
                },
                '.ck-reset_all': {
                  display: 'none',
                },
              }}
            >
              <CKEditor
                editor={ClassicEditor}
                data={content}
                disabled
                config={{
                  toolbar: [],
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('workflows.instructions.close')}</Button>
        {!readMode && (
          <Button
            onClick={() => {
              onEditStepField(String(stepId), 'comment', content);
              onClose();
            }}
            variant="contained"
            color="primary"
          >
            {t('workflows.instructions.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CommentEditorModal;
